import React from 'react'

import {FormikProps} from 'formik'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'
import * as Yup from 'yup'

import config from '../../../config'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {PossiblyMixedValue, isMixedValue, unlockMixedValuesLabel} from '../../utils/form'

import {Provider} from '../../../types'

import plusIcon from '../../../../../commons/src/assets/icons/plus.svg'

import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import InputField from '../../../../../commons/src/components/input-field'
import LinkButton from '../../../../../commons/src/components/link-button'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import Repeatable from '../../../../../commons/src/components/repeatable'
import MixedValueFormSection from '../../../components/mixed-value-form-section'
import MixedValueInputWrapper from '../../../components/mixed-value-input-wrapper'

export type QuantityThreshold = {
  axisSize: string
  qtyThreshold: string
}

export type SingleFormData = {
  name: string
  increaseSteps: string
  maxIncrease: string
  quantityThresholds: QuantityThreshold[]
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  name: PossiblyMixedValue<string>
  increaseSteps: PossiblyMixedValue<string>
  maxIncrease: PossiblyMixedValue<string>
  quantityThresholds: PossiblyMixedValue<QuantityThreshold[]>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues: SingleFormData = {
  name: '',
  increaseSteps: '',
  maxIncrease: '',
  quantityThresholds: [{axisSize: '0', qtyThreshold: ''}],
}

export const validationSchema = {
  name: Yup.string().label('Name').required(),
  increaseSteps: Yup.number()
    .min(1)
    .max(config.dynamicProductionTimes.maxIncreaseSteps)
    .label('Increase steps')
    .required(),
  maxIncrease: Yup.number()
    .min(1)
    .max(config.dynamicProductionTimes.maxIncrease)
    .label('Max increase')
    .required(),
  quantityThresholds: Yup.array()
    .of(
      Yup.object({
        axisSize: Yup.number().label('Axis size').required(),
        qtyThreshold: Yup.number().label('Quantity threshold').required(),
      })
    )
    .test('UniquAxisSizes', 'Axis sizes must be different', (quantityThresholds, ctx) => {
      if (quantityThresholds) {
        const uniqueData = uniqBy(quantityThresholds, 'axisSize')

        const isUnique = quantityThresholds.length === uniqueData.length

        if (isUnique) {
          return true
        }
        const nonUniqueIndex = quantityThresholds.findIndex(
          (row, i) => (!!row && row.axisSize) !== (uniqueData[i] ? uniqueData[i].axisSize : '')
        )

        return ctx.createError({
          path: `${ctx.path}.${nonUniqueIndex}.${'axisSize'}`,
        })
      }
      return true
    })
    .test(
      'QtyThresholdGTZero ',
      'Quantity threshold must be greater than 0',
      (quantityThresholds, ctx) => {
        if (quantityThresholds) {
          const invalidQtyThresholds = quantityThresholds.filter(row => row.qtyThreshold <= 0)
          if (isEmpty(invalidQtyThresholds)) {
            return true
          }
          const invalidQtyThresholdIndex = quantityThresholds.findIndex(
            row => row.qtyThreshold <= 0
          )
          return ctx.createError({
            path: `${ctx.path}.${invalidQtyThresholdIndex}.${'qtyThreshold'}`,
          })
        }
        return true
      }
    )
    .required(),
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  provider: Provider
}

const DynamicProductionTimeForm = ({formik, provider}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const getInitialQuantityThreshold = (index: number) => ({
    axisSize: index === 0 ? '0' : '',
    qtyThreshold: '',
  })

  const setPossiblyMixedQuantityThresholds = () => {
    formik.setFieldValue('quantityThresholds', [getInitialQuantityThreshold(0)])
  }

  const renderQuantityThresholdRow = (item: QuantityThreshold, index: number) => (
    <FormRow key={index} layout="half-half">
      {/* AXIS SIZE */}
      <FieldNote
        type="error"
        note={formik.submitCount > 0 && get(formik.errors, `quantityThresholds[${index}].axisSize`)}
      >
        <NumberInputField
          allowDecimals
          label="Axis size"
          name={`quantityThresholds[${index}].axisSize`}
          value={item.axisSize}
          suffix="mm"
          disabled={index === 0}
          error={
            formik.submitCount > 0 && !!get(formik.errors, `quantityThresholds[${index}].axisSize`)
          }
          onChange={(value, name) => formik.setFieldValue(name as string, value)}
          onBlur={formik.handleBlur}
        />
      </FieldNote>
      {/* QUANTITY THRESHOLD */}
      <FieldNote
        type="error"
        note={
          formik.submitCount > 0 && get(formik.errors, `quantityThresholds[${index}].qtyThreshold`)
        }
      >
        <NumberInputField
          allowDecimals
          label="Quantity threshold"
          name={`quantityThresholds[${index}].qtyThreshold`}
          value={item.qtyThreshold}
          error={
            formik.submitCount > 0 &&
            !!get(formik.errors, `quantityThresholds[${index}].qtyThreshold`)
          }
          onChange={(value, name) => formik.setFieldValue(name as string, value)}
          onBlur={formik.handleBlur}
        />
      </FieldNote>
    </FormRow>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      {/* NAME */}
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.name}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.name)}
            onClick={() => formik.setFieldValue('name', initialValues.name)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <InputField
              label="Name (for your internal reference only)"
              name="name"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.name)}
              value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
              error={formik.submitCount > 0 && !!formik.errors.name}
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      <Headline label="Increase steps" classNames={['u-margin-top-l']} />
      {/* INCREASE STEPS */}
      <FormRow layout="half-half">
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.increaseSteps}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.increaseSteps)}
            onClick={() => formik.setFieldValue('increaseSteps', initialValues.increaseSteps)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <NumberInputField
              allowDecimals
              label="Number of days"
              name="increaseSteps"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.increaseSteps)}
              value={
                isMixedValue(formik.values.increaseSteps)
                  ? initialValues.increaseSteps
                  : formik.values.increaseSteps
              }
              error={formik.submitCount > 0 && !!formik.errors.increaseSteps}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
        {/* MAX INCREASE */}
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.maxIncrease}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.maxIncrease)}
            onClick={() => formik.setFieldValue('maxIncrease', initialValues.maxIncrease)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <NumberInputField
              allowDecimals
              label="Max number of days"
              name="maxIncrease"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.maxIncrease)}
              value={
                isMixedValue(formik.values.maxIncrease)
                  ? initialValues.maxIncrease
                  : formik.values.maxIncrease
              }
              error={formik.submitCount > 0 && !!formik.errors.maxIncrease}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      {/* QUANTITY THRESHOLDS */}
      <Headline label="Quantity thresholds" classNames={['u-margin-top-l']} />
      <MixedValueFormSection
        locked={isMixedValue(formik.values.quantityThresholds)}
        onClick={setPossiblyMixedQuantityThresholds}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow>
          <Repeatable
            preventDeleteMin
            min={1}
            button={<LinkButton icon={<Icon source={plusIcon} />}>Add threshold</LinkButton>}
            onAdd={nextIndex => {
              formik.setFieldValue('quantityThresholds', [
                ...(formik.values.quantityThresholds as QuantityThreshold[]),
                getInitialQuantityThreshold(nextIndex),
              ])
            }}
            onDelete={index => {
              formik.setFieldValue(
                'quantityThresholds',
                (formik.values.quantityThresholds as QuantityThreshold[]).filter(
                  (_, i) => i !== index
                )
              )
            }}
          >
            {!isMixedValue(formik.values.quantityThresholds) &&
              (formik.values.quantityThresholds as QuantityThreshold[]).map((item, index) =>
                renderQuantityThresholdRow(item, index)
              )}
            {isMixedValue(formik.values.quantityThresholds) &&
              [getInitialQuantityThreshold(0)].map((item, index) =>
                renderQuantityThresholdRow(item, index)
              )}
          </Repeatable>
        </FormRow>
      </MixedValueFormSection>
    </form>
  )
}
export default DynamicProductionTimeForm
